/* eslint-disable consistent-return */
import React, { useEffect } from 'react'
import { graphql } from 'gatsby'

// Components
import Layout from 'components/Layout'
import SEO from 'components/SEO'

// CSS
import 'styles/Blog.scss'

// Components
import BlogErvaringHeader from 'components/PreviewContainer/BlogErvaringHeader'
import Specialist from 'components/Specialist'
import ParseContent from 'components/ParseContent'

// Third Party
import parse from 'html-react-parser'
import CustomLink from '../components/CustomLink'

export const BlogTemplate = ({
  title,
  blogImage,
  content,
  specialist,
  h1Tag,
}) => {
  const location = ''

  return (
    <section className="section blog-detail">
      <h1 className="d-none">{parse(h1Tag)}</h1>
      <div className="blog-detail-thin-line d-lg-block d-none color-background-contrast" />
      <BlogErvaringHeader
        className="pb-5 pb-lg-0"
        image={blogImage}
        title={title}
        location={location}
      />
      <div className="blog-detail-thin-line d-lg-block d-none color-background-secondary" />
      <div className="container blog-container">
        <div className="row">
          <div className="col-12 pt-3 pb-2 back-to-blog">
            <CustomLink to="/blog" className="font-weight-xl font-size-sm">
              &gt; Terug naar blogoverzicht
            </CustomLink>
          </div>
          <div className="col-12 col-lg-8">
            <div className="pt-2 pb-5 blog-detail-content">
              <ParseContent content={content} />
            </div>
          </div>
          <div className="col-12 col-lg-4 pt-2 d-flex justify-content-center justify-content-lg-end">
            <Specialist specialist={specialist} />
          </div>
        </div>

        <div className="empty-space-100" />
      </div>
    </section>
  )
}

const Blog = ({ data }) => {
  const { wordpressWpNieuws: blog } = data

  // add BlogPosting schema markup. to head
  const blogPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://koolooszonenhuis.nl/blog/${blog.slug}`,
    },
    headline: blog.title,
    description: blog.acf.blog_content.small_introduction,
    image: blog.acf.blog_content.image.localFile.childImageSharp.fixed.src,
    author: {
      '@type': 'Person',
      name: blog.acf.blog_content.specialist[0].title,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Kooloos Zon en Huis',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.koolooszonenhuis.nl/static/logo-d77c2a2ff818dc7f7812d15416999d82.svg',
      },
    },
    datePublished: '',
  }

  const schemaMarkup = JSON.stringify(blogPostingSchema)

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.innerHTML = schemaMarkup
    document.head.appendChild(script)
    return () => {
      document.head.removeChild(script)
    }
  }, [schemaMarkup])

  // add canonical link to head

  useEffect(() => {
    const canonicalLink = document.createElement('link')
    canonicalLink.rel = 'canonical'
    canonicalLink.href =
      `https://koolooszonenhuis.nl${blog.yoast_head_json.canonical}` ||
      `https://koolooszonenhuis.nl/blog/${blog.slug}`
    document.head.appendChild(canonicalLink)

    return () => {
      document.head.removeChild(canonicalLink)
    }
  }, [])

  return (
    <Layout>
      {blog.acf.blog_content.image.localFile.childImageSharp.fixed.src ? (
        <SEO
          yoast={{ meta: blog.yoast_meta }}
          pathname={`/blog/${blog.slug}`}
          image={
            blog.acf.blog_content.image.localFile.childImageSharp.fixed.src
          }
          article
        />
      ) : (
        <SEO
          yoast={{ meta: blog.yoast_meta }}
          pathname={`/blog/${blog.slug}`}
          article
        />
      )}
      <BlogTemplate
        title={blog.title}
        blogImage={blog.acf.blog_content.image.localFile.childImageSharp.fluid}
        content={blog.acf.blog_content.story}
        specialist={blog.acf.blog_content.specialist}
        h1Tag={blog.acf.blog_content.h1_tag}
      />
    </Layout>
  )
}

export default Blog

export const pageQuery = graphql`
  fragment BLogPostFields on wordpress__wp_nieuws {
    id
    slug
  }
  query DetailBlogQuery($id: String!) {
    wordpressWpNieuws(id: { eq: $id }) {
      title
      slug
      yoast_meta {
        content
        name
        property
      }
      yoast_head_json {
        canonical
      }
      acf {
        blog_content {
          small_introduction
          story
          image {
            localFile {
              childImageSharp {
                fluid(quality: 100, maxWidth: 1920) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
                fixed(width: 1024, toFormat: JPG) {
                  src
                }
              }
            }
          }
          specialist {
            title
            slug
            acf {
              number
              specialist_text
              image {
                localFile {
                  childImageSharp {
                    fluid(quality: 100, maxWidth: 250) {
                      ...GatsbyImageSharpFluid_withWebp
                    }
                  }
                }
              }
            }
          }
          h1_tag
        }
      }
    }
  }
`
